import { useEffect, useMemo, useState } from "react";
import { Breadcrumb, Button } from "flowbite-react";
import { HiHome, HiPlus } from "react-icons/hi";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { DataGridPagination, QueryParams } from "@/sharedTypes";
import {
  useCreateAgentMutation,
  useGetAgentsQuery,
  useLazyGetAgentsQuery,
} from "@/api/agentApi/agent";
import ExportMenu from "@/components/common/ExportMenu";
import { fetchAllPages } from "@/utils/listExport";
import {
  useGetAgentsInMyGroupsQuery,
  useLazyGetAgentsInMyGroupsQuery,
} from "@/api/groupApi/agentGroup";
import { useAddSubcategorySkillsMutation } from "@/api/agentApi/agentSkill";
import AgentListTable from "@/components/agents/AgentListTable";
import AgentFormDialog from "@/components/agents/AgentFormDialog";
import LoadingPage from "@/components/LoadingPage";
import { toast } from "@/lib/toast";
import { flattenQueryParams, getErrorMessage } from "@/utils/requestUtils";
import SubcategorySkillDialog from "@/components/agents/SubcategorySkillDialog";
import { agentRoles, isCompanyAdminUser } from "@/constants/agent";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useEffectiveRoles, hasAnyRole } from "@/hooks/useEffectiveRoles";

export default function AgentListPage() {
  const { t } = useTranslation(["agent", "common"]);
  const user = useCurrentUser();
  const effectiveRoles = useEffectiveRoles();
  // Managers (Yönetici) see all agents. company_admin can additionally create.
  const isManager = hasAnyRole(effectiveRoles, [
    agentRoles.management,
    agentRoles.company_admin,
    agentRoles.super_admin,
  ]);
  const isCompanyAdmin = isCompanyAdminUser(user);

  const [queryParams, setQueryParams] = useState<QueryParams>({
    filters: { is_active: true },
    pagination: { page: 1, pageSize: 15 },
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSkillDialogOpen, setIsSkillDialogOpen] = useState(false);
  const [newAgentDetails, setNewAgentDetails] = useState<{
    agent_id: number;
  } | null>(null);

  const flatParams = flattenQueryParams(queryParams);

  // Always fetch the current user's group members for non-managers — this also
  // tells us whether the current user is a Group Admin (is_admin on their own
  // membership), which lets them see the full agent list too.
  const {
    data: agentsInMyGroups,
    isLoading: isLoadingMyGroups,
    isError: isErrorMyGroups,
    error: errorMyGroups,
  } = useGetAgentsInMyGroupsQuery(queryParams, {
    skip: isManager,
    refetchOnMountOrArgChange: true,
  });

  const isGroupAdmin = useMemo(() => {
    const items = agentsInMyGroups?.items || [];
    const me = items.find((a: any) => a.agent_id === user?.agent_id);
    return !!me && (me.is_admin === 1 || me.is_admin === true);
  }, [agentsInMyGroups, user?.agent_id]);

  // Managers and Group Admins see every agent; everyone else only their group.
  const canSeeAll = isManager || isGroupAdmin;

  const {
    data: agentsData,
    isLoading: isLoadingAll,
    isError: isErrorAll,
    error: errorAll,
  } = useGetAgentsQuery(flatParams, {
    skip: !canSeeAll,
    refetchOnMountOrArgChange: true,
  });

  const activeData = canSeeAll ? agentsData : agentsInMyGroups;
  const isLoading = canSeeAll ? isLoadingAll : isLoadingMyGroups;
  const isError = canSeeAll ? isErrorAll : isErrorMyGroups;
  const error = canSeeAll ? errorAll : errorMyGroups;

  const [fetchAllAgents] = useLazyGetAgentsQuery();
  const [fetchMyGroupAgents] = useLazyGetAgentsInMyGroupsQuery();

  // Follows the same visibility rule as the table: managers and group admins
  // export every agent, everyone else only their own group.
  const fetchExportRows = () =>
    fetchAllPages((page, pageSize) => {
      const pagination = { page, pageSize };
      return canSeeAll
        ? fetchAllAgents(flattenQueryParams({ ...queryParams, pagination })).unwrap()
        : fetchMyGroupAgents({ ...queryParams, pagination }).unwrap();
    });

  const exportFilters = Object.entries(queryParams.filters ?? {})
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => ({
      label: k === "is_active" ? t("common:status") : t(`agent:${k}`, { defaultValue: k }),
      value: k === "is_active" ? (v ? t("common:active") : t("common:inactive")) : String(v),
    }));

  const [createAgent] = useCreateAgentMutation();
  const [addSkills] = useAddSubcategorySkillsMutation();

  useEffect(() => {
    if (isError) {
      toast.error(getErrorMessage(error));
    }
  }, [isError, error]);

  const handleCreate = (data: any) => {
    const payload: Record<string, any> = {
      agent_username: data.agent_username,
      agent_email: data.agent_email,
      agent_password: data.agent_password,
      display_name: data.display_name,
      shift_id: data.shift_id ? parseInt(data.shift_id, 10) : undefined,
    };

    const groupIds: number[] = data.group_ids || [];
    if (groupIds.length > 0) {
      payload.group_ids = groupIds;
    }

    if (data.job_roles) {
      payload.job_roles = data.job_roles;
    }

    createAgent(payload)
      .unwrap()
      .then((created: any) => {
        const createdId = created?.agent_id ?? created?.data?.agent_id;
        if (createdId) {
          setNewAgentDetails({ agent_id: createdId });
          setIsSkillDialogOpen(true);
        }

        const skipped = created?.skipped_groups ?? created?.data?.skipped_groups;
        if (skipped?.length) {
          toast.warning(t("agent:someGroupsSkipped"));
        }

        toast.success(t("agent:createSuccess"));
        setIsCreateModalOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleAssignSkills = (
    skills: { subcategory_id: number; skill_rate: number }[]
  ) => {
    if (!newAgentDetails?.agent_id) return;
    addSkills({ agent_id: newAgentDetails.agent_id, skills })
      .unwrap()
      .then(() => {
        toast.success(t("agent:addSkillsSuccess"));
        setIsSkillDialogOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handlePageChange = (page: number) => {
    setQueryParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, page },
    }));
  };

  const pagination: DataGridPagination = useMemo(() => {
    const pagination = activeData?.pagination || {};
    return {
      pageIndex:
        pagination.pageIndex ?? pagination.page ?? queryParams.pagination.page,
      pageSize: pagination.pageSize ?? queryParams.pagination.pageSize,
      totalPages: pagination.totalPages ?? 1,
      totalItems: pagination.totalItems ?? pagination.total ?? 0,
    };
  }, [activeData, queryParams.pagination.page, queryParams.pagination.pageSize]);

  return (
    <NavbarSidebarLayout>
      {isLoading ? <LoadingPage /> : <div className="h-screen overflow-y-auto pb-20">
        <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
          <div className="col-span-full space-y-6">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">{t("common:home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("agent:agents")}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  {t("agent:agents")}
                </h1>
                <p className="text-gray-500">{t("agent:manageAgentsDescription")}</p>
              </div>
              <div className="flex items-center gap-2">
                <ExportMenu
                  title={t("agent:agents")}
                  filename="temsilciler"
                  filters={exportFilters}
                  fetchRows={fetchExportRows}
                  columns={[
                    { header: "ID", value: (r: any) => r.agent_id ?? "" },
                    { header: t("agent:displayName"), value: (r: any) => r.display_name ?? "" },
                    { header: t("agent:username"), value: (r: any) => r.agent_username ?? "" },
                    { header: t("agent:email"), value: (r: any) => r.agent_email ?? "" },
                    { header: t("common:status"), value: (r: any) => (r.is_active ? t("common:active") : t("common:inactive")) },
                  ]}
                />
                {isCompanyAdmin && (
                  <Button
                    color="primary"
                    onClick={() => setIsCreateModalOpen(true)}
                  >
                    <HiPlus className="mr-2 h-5 w-5" />
                    {t("agent:createAgent")}
                  </Button>
                )}
              </div>
            </div>

            <AgentListTable
              data={activeData?.items || []}
              pagination={pagination}
              onPageChange={handlePageChange}
              filters={queryParams.filters}
              onFilterChange={(newFilters) =>
                setQueryParams((prev) => ({
                  ...prev,
                  filters: newFilters,
                  pagination: { ...prev.pagination, page: 1 },
                }))
              }
            />
          </div>
        </div>
      </div>}

      <AgentFormDialog
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
        mode="create"
      />

      <SubcategorySkillDialog
        open={isSkillDialogOpen}
        onClose={() => setIsSkillDialogOpen(false)}
        onSubmit={handleAssignSkills}
      />
    </NavbarSidebarLayout>
  );
}
